import { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { api, getToken, setToken, clearToken, alVencerSesion, alCerrarseSeccion } from '../lib/api';
import { onHubEvent } from '../lib/realtime';
import { Preferencias, PREFERENCIAS_DEFAULT, limpiarPreferencias } from '../lib/preferencias';

export interface HubUser {
  name: string;
  role: string; // 'admin' | 'asesora' | 'medico' | 'cosmetologa' | 'recepcion' | 'marketing' (restringido: solo galeria)
  tenantId: string;
  tenantName: string;
  scope: 'all' | 'tenant'; // 'all' = master_admin (ve todas las clínicas)
  /** Qué secciones abre (Administración → Permisos, api/src/lib/permisos.ts). Una cuenta de admin: todas. */
  secciones?: string[];
}

interface AuthState {
  user: HubUser | null;
  loading: boolean;
  /** Las preferencias de la clínica (Administración → Preferencias); llegan con la sesión, antes de pintar nada. */
  preferencias: Preferencias;
  setPreferencias: (p: Preferencias) => void;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<HubUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [preferencias, setPrefsEstado] = useState<Preferencias>(PREFERENCIAS_DEFAULT);
  const setPreferencias = (p: Preferencias) => setPrefsEstado(limpiarPreferencias(p));

  // Al cargar, si hay token, valida con /auth/me.
  useEffect(() => {
    const token = getToken();
    if (!token) { setLoading(false); return; }
    api<SesionResp>('auth/session')
      .then((me) => {
        setPreferencias(me.preferencias || PREFERENCIAS_DEFAULT);
        setUser(deSesion(me));
      })
      .catch(() => clearToken())
      .finally(() => setLoading(false));
  }, []);

  async function login(email: string, password: string) {
    const res = await api<{ token: string; user: HubUser; preferencias?: Preferencias }>('auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    setToken(res.token);
    setPreferencias(res.preferencias || PREFERENCIAS_DEFAULT);
    setUser(res.user);
  }

  function logout() {
    clearToken();
    setUser(null);
    setPrefsEstado(PREFERENCIAS_DEFAULT);
  }

  // La sesión se cierra todos los días a las 3:00 a.m.: cuando el servidor contesta `sesion_vencida`, lib/api.ts lo dice con el diálogo de la app
  // y, al cerrarlo, aquí se sale (las rutas protegidas mandan al inicio de sesión).
  useEffect(() => { alVencerSesion(() => logout()); return () => alVencerSesion(null); }, []);

  // Permisos por sección (0.55.0): si un admin los cambia con la pantalla abierta, el menú y las rutas se ponen al día
  // solos — cuando el API contesta "esa sección está cerrada", cuando llega el aviso de tiempo real de que cambió la
  // configuración y al volver a la pestaña (como mucho una vez por minuto). Una cuenta de admin abre todo: no hace falta.
  const esAdmin = user?.role === 'admin';
  const conSesion = !!user;
  useEffect(() => {
    if (!conSesion || esAdmin) return;
    let ultima = Date.now();
    let vivo = true;
    const refrescar = (minMs: number) => {
      if (!getToken() || Date.now() - ultima < minMs) return;
      ultima = Date.now();
      api<SesionResp>('auth/session')
        .then((me) => { if (vivo) setUser((u) => (u && (u.secciones || []).join() === (me.secciones || []).join() ? u : deSesion(me))); })
        .catch(() => { /* el 401 de una sesión vencida ya lo atiende lib/api.ts */ });
    };
    alCerrarseSeccion(() => refrescar(3000));
    const alVolver = () => { if (document.visibilityState === 'visible') refrescar(60000); };
    document.addEventListener('visibilitychange', alVolver);
    const off = onHubEvent((e) => { if (e.type === 'config') setTimeout(() => refrescar(2000), 1500); });
    return () => { vivo = false; alCerrarseSeccion(null); document.removeEventListener('visibilitychange', alVolver); off(); };
  }, [conSesion, esAdmin]);

  const value = useMemo(() => ({ user, loading, login, logout, preferencias, setPreferencias }), [user, loading, preferencias]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

interface SesionResp { name: string; rol: string; tenant_id: string; tenantName: string; scope: 'all' | 'tenant'; preferencias?: Preferencias; secciones?: string[] }
const deSesion = (me: SesionResp): HubUser => ({ name: me.name, role: me.rol, tenantId: me.tenant_id, tenantName: me.tenantName, scope: me.scope, secciones: me.secciones });

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de <AuthProvider>');
  return ctx;
}
